import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  crmAddComment,
  crmAddFollowUp,
  crmAttendEnd,
  crmAttendStart,
  crmFileBlobUrl,
  crmGetAttend,
  crmGetAttachments,
  crmGetComments,
  crmGetFollowUps,
  crmGetStaff,
  crmUpload,
  waMeUrl,
} from "../../../../api/crmApi";
import GetLocalStorageUserId from "../../../sharedComponents/GetLocalStorageuserId";

export const STAGES = ["New Lead", "Interested", "Confirmed", "Cancelled"];
export const TASK_STATUSES = ["Open", "In progress", "Done"];
export const TASK_PRIORITIES = ["High", "Medium", "Low"];
export const LEAD_SOURCES = ["WhatsApp", "Facebook", "Instagram", "TikTok", "Call", "Walk-in", "Other"];
export const CALL_OUTCOMES = ["Connected", "No answer", "Callback", "Voicemail", "Confirmed"];
export const CARRIER_STATUSES = ["Booked", "In transit", "Out for delivery", "Delivered", "Cancelled"];
export const ROUTES = ["BD to UK", "UK to BD", "UK-UK", "UK to Canada", "Bangladesh - UK", "UK - Bangladesh"];

export function priorityClass(priority) {
  const p = String(priority || "Medium").toLowerCase();
  if (p === "high") return "bg-red-100 text-red-700 border-red-200";
  if (p === "low") return "bg-sky-100 text-sky-700 border-sky-200";
  return "bg-amber-100 text-amber-800 border-amber-200";
}

export function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="font-semibold text-stone-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function TextInput({ value, onChange, type = "text" }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-stone-300 pl-3 h-10 outline-none rounded-xl bg-stone-50 focus:bg-white focus:border-orange-500"
    />
  );
}

export function SelectInput({ value, onChange, options, allowEmpty = true }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-stone-300 pl-3 pr-8 h-10 outline-none rounded-xl bg-stone-50 focus:bg-white focus:border-orange-500 admin-select"
    >
      {allowEmpty ? <option value="">Select</option> : null}
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

export function PrimaryBtn({ children, onClick, type = "button", disabled }) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-xl h-10 px-4 text-sm font-medium"
    >
      {children}
    </button>
  );
}

export function GhostBtn({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border border-stone-300 text-stone-700 rounded-xl h-10 px-4 text-sm font-medium bg-white"
    >
      {children}
    </button>
  );
}

export function CrmDrawer({ title, onClose, children, wide, footer }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className={`bg-white w-full ${wide ? "max-w-5xl" : "max-w-2xl"} max-h-[90vh] shadow-xl rounded-2xl flex flex-col`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 shrink-0">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="text-stone-500 text-sm hover:text-stone-800">
            Close
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4 flex-1">{children}</div>
        {footer ? (
          <div className="px-5 py-3 border-t border-stone-200 bg-stone-50 rounded-b-2xl flex flex-wrap gap-2 shrink-0">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function staffLabel(u) {
  const name = u?.userName || u?.UserName || u?.name || u?.Name || "";
  const email = u?.email || u?.Email || "";
  if (name && email && name !== email) return `${name} · ${email}`;
  return name || email || "Unknown";
}

function staffSearchText(u) {
  return [
    u?.userName,
    u?.UserName,
    u?.email,
    u?.Email,
    u?.name,
    u?.Name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function StaffSearchSelect({ staff = [], selectedIds = [], onChange, placeholder = "Search staff…", emptyHint = "No one assigned yet. Search and select a person." }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = selectedIds || [];

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (staff || [])
      .filter((u) => !term || staffSearchText(u).includes(term))
      .slice(0, 80);
  }, [staff, query]);

  const selectedPeople = useMemo(
    () => (staff || []).filter((u) => selected.includes(u.id || u.Id)),
    [staff, selected],
  );

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full border border-stone-300 pl-3 h-10 outline-none rounded-xl bg-stone-50 focus:bg-white focus:border-orange-500"
        autoComplete="off"
      />
      {open ? (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-stone-200 bg-white shadow-lg">
          {matches.map((u) => {
            const id = u.id || u.Id;
            const on = selected.includes(id);
            return (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 ${
                    on ? "bg-orange-100 font-medium" : ""
                  }`}
                >
                  {staffLabel(u)}
                </button>
              </li>
            );
          })}
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-sm text-stone-400">No matches</li>
          ) : null}
        </ul>
      ) : null}
      {selectedPeople.length > 0 ? (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedPeople.map((u) => {
            const id = u.id || u.Id;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 text-xs rounded-full px-2 py-1 bg-orange-100 border border-orange-300"
              >
                {staffLabel(u)}
                <button type="button" className="text-stone-500" onClick={() => toggle(id)} aria-label="Remove">
                  ×
                </button>
              </span>
            );
          })}
        </div>
      ) : emptyHint ? (
        <p className="text-xs text-stone-400 mt-1">{emptyHint}</p>
      ) : null}
    </div>
  );
}

export function AttendBar({ relatedType, relatedId, phone }) {
  const [attend, setAttend] = useState(null);
  const me = String(GetLocalStorageUserId() || "");

  const reload = async () => {
    if (!relatedId) return;
    const row = await crmGetAttend(relatedType, relatedId);
    setAttend(row);
  };

  useEffect(() => {
    reload().catch(() => setAttend(null));
  }, [relatedType, relatedId]);

  const wa = waMeUrl(phone);
  const open = attend && !attend.endedAt && !attend.EndedAt;
  const name = attend?.userName || attend?.UserName;
  const uid = String(attend?.userId || attend?.UserId || "");

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm mb-1">
      {open ? (
        <span className="text-stone-700">
          Attended by <strong>{name || "staff"}</strong>
        </span>
      ) : (
        <span className="text-stone-500">Not being attended</span>
      )}
      {(!open || uid === me) && (
        <button
          type="button"
          className="h-8 px-3 rounded-lg bg-orange-600 text-white text-xs font-semibold"
          onClick={async () => {
            if (open) await crmAttendEnd(relatedType, relatedId);
            else await crmAttendStart(relatedType, relatedId);
            await reload();
          }}
        >
          {open ? "Release" : "Attend"}
        </button>
      )}
      {wa ? (
        <a
          href={wa}
          target="_blank"
          rel="noreferrer"
          className="h-8 px-3 rounded-lg bg-green-600 text-white text-xs font-semibold inline-flex items-center"
        >
          WhatsApp
        </a>
      ) : null}
    </div>
  );
}

export function CommentThread({ relatedType, relatedId }) {
  const [comments, setComments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [body, setBody] = useState("");
  const [mentionIds, setMentionIds] = useState([]);

  const reload = async () => {
    if (!relatedId) return;
    setComments(await crmGetComments(relatedType, relatedId));
  };

  useEffect(() => {
    reload().catch(() => setComments([]));
    crmGetStaff().then(setStaff).catch(() => setStaff([]));
  }, [relatedType, relatedId]);

  return (
    <div className="mt-4">
      <p className="font-semibold mb-2">Comments</p>
      <div className="space-y-2 max-h-56 overflow-y-auto mb-3">
        {comments.map((c) => (
          <div key={c.id || c.Id} className="rounded-lg border border-stone-200 p-2 text-sm">
            <div className="text-xs text-stone-500">
              {c.authorName || c.AuthorName} · {new Date(c.creationDate || c.CreationDate).toLocaleString()}
            </div>
            <div className="whitespace-pre-wrap">{c.body || c.Body}</div>
          </div>
        ))}
        {comments.length === 0 ? <p className="text-sm text-stone-400">No comments yet.</p> : null}
      </div>
      <textarea
        className="w-full border border-stone-300 rounded-xl p-2 text-sm"
        rows={3}
        placeholder="Write a comment. Optionally tag someone below."
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <div className="my-2">
        <StaffSearchSelect
          staff={staff}
          selectedIds={mentionIds}
          onChange={setMentionIds}
          placeholder="Tag someone (optional)"
          emptyHint=""
        />
      </div>
      <PrimaryBtn
        disabled={!body.trim() || !relatedId}
        onClick={async () => {
          const names = staff
            .filter((u) => mentionIds.includes(u.id || u.Id))
            .map((u) => u.userName || u.UserName);
          await crmAddComment({
            relatedType,
            relatedId,
            body,
            mentionUserIds: mentionIds,
            mentionUserNames: names,
          });
          setBody("");
          setMentionIds([]);
          await reload();
        }}
      >
        Post comment
      </PrimaryBtn>
    </div>
  );
}

export function AttachmentList({ relatedType, relatedId }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState({});

  const reload = async () => {
    if (!relatedId) return;
    const list = await crmGetAttachments(relatedType, relatedId);
    setFiles(list);
    const next = {};
    await Promise.all(
      list.map(async (f) => {
        const id = f.id || f.Id;
        const type = f.contentType || f.ContentType || "";
        try {
          next[id] = await crmFileBlobUrl(id);
        } catch {
          next[id] = "";
        }
        if (!type.startsWith("image/") && next[id] === undefined) {
          next[id] = "";
        }
      }),
    );
    setPreviews((prev) => {
      Object.values(prev).forEach((url) => url && URL.revokeObjectURL(url));
      return next;
    });
  };

  useEffect(() => {
    reload().catch(() => setFiles([]));
    return () => {
      setPreviews((prev) => {
        Object.values(prev).forEach((url) => url && URL.revokeObjectURL(url));
        return {};
      });
    };
  }, [relatedType, relatedId]);

  return (
    <div className="mt-4">
      <p className="font-semibold mb-2">Files / images</p>
      <input
        type="file"
        accept="image/*,.pdf,.csv,.xlsx,.xls"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file || !relatedId) return;
          await crmUpload(file, relatedType, relatedId);
          e.target.value = "";
          await reload();
        }}
      />
      <ul className="mt-2 space-y-1 text-sm">
        {files.map((f) => {
          const id = f.id || f.Id;
          const name = f.fileName || f.FileName;
          const type = f.contentType || f.ContentType || "";
          const url = previews[id];
          return (
            <li key={id}>
              {type.startsWith("image/") && url ? (
                <img
                  alt={name}
                  src={url}
                  className="max-h-28 rounded-lg border border-stone-200 mb-1"
                />
              ) : null}
              {url ? (
                <a href={url} target="_blank" rel="noreferrer" className="text-orange-700">
                  {name}
                </a>
              ) : (
                <span>{name}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function ImportCsvButton({ label, onParsed }) {
  return (
    <label className="border border-stone-300 text-stone-700 rounded-xl h-10 px-4 text-sm font-medium bg-white inline-flex items-center cursor-pointer">
      {label}
      <input
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const text = await file.text();
          await onParsed(text);
          e.target.value = "";
        }}
      />
    </label>
  );
}

export function FollowUpLog({ queryId }) {
  const [rows, setRows] = useState([]);
  const [outcome, setOutcome] = useState("Connected");
  const [customerSaid, setCustomerSaid] = useState("");
  const [feedback, setFeedback] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    if (!queryId) return;
    setRows(await crmGetFollowUps(queryId));
  };

  useEffect(() => {
    reload().catch(() => setRows([]));
  }, [queryId]);

  if (!queryId) return null;

  return (
    <div className="h-full flex flex-col">
      <p className="font-semibold text-stone-800 mb-3">Call log</p>
      <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 mb-3 space-y-2">
        <SelectInput value={outcome} onChange={setOutcome} options={CALL_OUTCOMES} allowEmpty={false} />
        <textarea
          className="w-full border border-stone-300 rounded-xl p-2 text-sm bg-white"
          rows={2}
          placeholder="What did the customer say?"
          value={customerSaid}
          onChange={(e) => setCustomerSaid(e.target.value)}
        />
        <textarea
          className="w-full border border-stone-300 rounded-xl p-2 text-sm bg-white"
          rows={2}
          placeholder="Your feedback"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />
        <TextInput value={nextAction} onChange={setNextAction} />
        <p className="text-[11px] text-stone-400 -mt-1">Next action (optional)</p>
        <PrimaryBtn
          disabled={saving || (!customerSaid.trim() && !feedback.trim())}
          onClick={async () => {
            setSaving(true);
            try {
              await crmAddFollowUp(queryId, {
                outcome,
                customerSaid,
                feedback,
                nextAction,
                calledAt: new Date().toISOString(),
              });
              setCustomerSaid("");
              setFeedback("");
              setNextAction("");
              await reload();
            } finally {
              setSaving(false);
            }
          }}
        >
          Log this call
        </PrimaryBtn>
      </div>
      <div className="space-y-2 overflow-y-auto flex-1 min-h-[120px] max-h-[280px]">
        {rows.map((r) => (
          <div key={r.id || r.Id} className="rounded-xl border border-stone-200 p-3 text-sm bg-white">
            <div className="flex items-center justify-between gap-2 text-xs text-stone-500">
              <span>{r.calledByName || r.CalledByName}</span>
              <span>{new Date(r.calledAt || r.CalledAt).toLocaleString()}</span>
            </div>
            <p className="mt-1 font-medium text-stone-800">{r.outcome || r.Outcome}</p>
            {r.customerSaid || r.CustomerSaid ? (
              <p className="mt-1 text-stone-700">{r.customerSaid || r.CustomerSaid}</p>
            ) : null}
            {r.feedback || r.Feedback ? (
              <p className="text-stone-500">{r.feedback || r.Feedback}</p>
            ) : null}
            {r.nextAction || r.NextAction ? (
              <p className="text-xs text-orange-700 mt-1">Next: {r.nextAction || r.NextAction}</p>
            ) : null}
          </div>
        ))}
        {rows.length === 0 ? (
          <p className="text-sm text-stone-400 px-1">No calls yet. Log the first follow-up above.</p>
        ) : null}
      </div>
    </div>
  );
}
